export interface ReceivingChainItem {
  index: number;
  delay: string;
  from: string;
  to: string;
  protocol: string;
  timeReceived: string;
}

export interface ParsedEmail {
  subject: string;
  html: string;
  text: string;
  from?: {
    address: string;
    name?: string;
  };
  to?: {
    address: string;
    name?: string;
  };
  date?: Date;
  messageId?: string;
  senderIP?: string;
  headers?: Record<string, string | string[]>;
  receivingChain?: ReceivingChainItem[];
}

/**
 * Parse raw email content using postal-mime (with fallback)
 */
export async function parseRawEmail(rawMail: string): Promise<ParsedEmail> {
  try {
    // Try to dynamically import postal-mime (default export)
    const postalMimeModule = await import("postal-mime");
    const PostalMime = postalMimeModule.default || postalMimeModule;
    const email = await PostalMime.parse(rawMail);

    // Convert headers array to record
    const headersRecord: Record<string, string | string[]> = {};
    if (email.headers) {
      email.headers.forEach((header) => {
        const key = header.key.toLowerCase();
        if (headersRecord[key]) {
          // If key exists, convert to array
          const existing = headersRecord[key];
          headersRecord[key] = Array.isArray(existing) 
            ? [...existing, header.value]
            : [existing, header.value];
        } else {
          headersRecord[key] = header.value;
        }
      });
    }

    // Extract sender IP from headers (check Received headers or Return-Path)
    const senderIP = extractSenderIP(headersRecord, email);

    // Parse receiving chain from Received headers
    const receivingChain = parseReceivingChain(headersRecord);

    // Ensure subject is fully extracted (postal-mime should handle this, but ensure no truncation)
    const fullSubject = email.subject ? email.subject.trim() : "";
    
    return {
      subject: fullSubject,
      html: email.html || email.text || "",
      text: email.text || "",
      from: email.from && "address" in email.from && email.from.address
        ? {
            address: email.from.address,
            name: email.from.name || undefined,
          }
        : undefined,
      to: email.to && email.to.length > 0 && "address" in email.to[0] && email.to[0].address
        ? {
            address: email.to[0].address,
            name: email.to[0].name || undefined,
          }
        : undefined,
      date: email.date ? new Date(email.date) : undefined,
      messageId: email.messageId || "",
      senderIP,
      headers: headersRecord,
      receivingChain,
    };
  } catch (error) {
    console.warn("postal-mime not available or parsing failed, using fallback parser:", error);
    // Fallback: try to extract basic info from raw mail
    return parseRawEmailFallback(rawMail);
  }
}

/**
 * Extract sender IP from email headers
 */
function extractSenderIP(headers: Record<string, string | string[]>, email: any): string | undefined {
  // Check Received-SPF header - contains client-ip
  const receivedSPF = headers["received-spf"];
  if (receivedSPF) {
    const receivedSPFArray = Array.isArray(receivedSPF) ? receivedSPF : [receivedSPF];
    for (const receivedSPFStr of receivedSPFArray) {
      const spfStr = typeof receivedSPFStr === "string" ? receivedSPFStr : String(receivedSPFStr);
      // Look for client-ip=IP pattern
      const clientIPMatch = spfStr.match(/client-ip=(\d+\.\d+\.\d+\.\d+)/i) || spfStr.match(/client-ip=([a-f0-9:]+)/i);
      if (clientIPMatch) return clientIPMatch[1];
      // Also check for "designates IP as permitted sender" pattern
      const designatesMatch = spfStr.match(/designates\s+(\d+\.\d+\.\d+\.\d+)\s+as\s+permitted\s+sender/i) || 
                              spfStr.match(/designates\s+([a-f0-9:]+)\s+as\s+permitted\s+sender/i);
      if (designatesMatch) return designatesMatch[1];
    }
  }

  // Check Authentication-Results header - contains SPF results with IP
  const authResults = headers["authentication-results"];
  if (authResults) {
    const authResultsArray = Array.isArray(authResults) ? authResults : [authResults];
    for (const authResultsStr of authResultsArray) {
      const authStr = typeof authResultsStr === "string" ? authResultsStr : String(authResultsStr);
      // Look for "designates IP as permitted sender" pattern in SPF results
      const designatesMatch = authStr.match(/designates\s+(\d+\.\d+\.\d+\.\d+)\s+as\s+permitted\s+sender/i) || 
                              authStr.match(/designates\s+([a-f0-9:]+)\s+as\s+permitted\s+sender/i);
      if (designatesMatch) return designatesMatch[1];
    }
  }

  // Check Return-Path header
  const returnPath = headers["return-path"];
  if (returnPath) {
    const returnPathStr = Array.isArray(returnPath) ? returnPath[0] : returnPath;
    const ipMatch = returnPathStr.match(/\[(\d+\.\d+\.\d+\.\d+)\]/i) || returnPathStr.match(/\[([a-f0-9:]+)\]/i);
    if (ipMatch) return ipMatch[1];
  }

  // Check X-Originating-IP or other IP headers
  const xOriginatingIP = headers["x-originating-ip"] || headers["x-sender-ip"];
  if (xOriginatingIP) {
    const ipStr = Array.isArray(xOriginatingIP) ? xOriginatingIP[0] : xOriginatingIP;
    const ipMatch = ipStr.match(/(\d+\.\d+\.\d+\.\d+)/i) || ipStr.match(/([a-f0-9:]+)/i);
    if (ipMatch) return ipMatch[1];
  }

  // Check Received headers - sender IP is typically in the LAST (oldest) Received header
  const received = headers["received"];
  if (received) {
    const receivedArray = Array.isArray(received) ? received : [received];
    // Received headers are in reverse chronological order (newest first in array)
    // The sender IP is in the FIRST element of the array (oldest chronologically, last added)
    if (receivedArray.length > 0) {
      // Check all Received headers, starting with the oldest (first in array)
      for (const receivedHeader of receivedArray) {
        const receivedStr = typeof receivedHeader === "string" ? receivedHeader : String(receivedHeader);
        // Look for IP in brackets: [IP] - try both IPv4 and IPv6
        const ipMatch = receivedStr.match(/\[(\d+\.\d+\.\d+\.\d+)\]/i) || receivedStr.match(/\[([a-f0-9:]+)\]/i);
        if (ipMatch) return ipMatch[1];
        // Also try IP in parentheses: (IP)
        const ipMatchParen = receivedStr.match(/\((\d+\.\d+\.\d+\.\d+)\)/i) || receivedStr.match(/\(([a-f0-9:]+)\)/i);
        if (ipMatchParen) return ipMatchParen[1];
      }
    }
  }

  // Check X-Received headers as well
  const xReceived = headers["x-received"];
  if (xReceived) {
    const xReceivedArray = Array.isArray(xReceived) ? xReceived : [xReceived];
    for (const xReceivedHeader of xReceivedArray) {
      const xReceivedStr = typeof xReceivedHeader === "string" ? xReceivedHeader : String(xReceivedHeader);
      const ipMatch = xReceivedStr.match(/\[(\d+\.\d+\.\d+\.\d+)\]/i) || xReceivedStr.match(/\[([a-f0-9:]+)\]/i);
      if (ipMatch) return ipMatch[1];
    }
  }

  return undefined;
}

/**
 * Parse receiving chain from Received headers
 */
function parseReceivingChain(headers: Record<string, string | string[]>): ReceivingChainItem[] {
  const received = headers["received"] || headers["x-received"];
  if (!received) return [];

  const receivedArray = Array.isArray(received) ? received : [received];
  const chain: ReceivingChainItem[] = [];
  let previousTime: Date | null = null;

  // Received headers are in reverse chronological order (newest first)
  // We need to reverse them to show oldest to newest
  const reversedReceived = [...receivedArray].reverse();

  reversedReceived.forEach((receivedHeader, index) => {
    const receivedStr = typeof receivedHeader === "string" ? receivedHeader : String(receivedHeader);
    
    // Parse Received header format:
    // Received: from hostname [IP] (hostname) by server with protocol id timestamp; date
    // Also handle: X-Received: by server [IP] with protocol; date
    const fromMatch = receivedStr.match(/from\s+([^\s\[\](]+)(?:\s*\[([^\]]+)\])?(?:\s*\([^)]+\))?/i);
    const byMatch = receivedStr.match(/by\s+([^\s\[\](]+)(?:\s*\[([^\]]+)\])?(?:\s*\([^)]+\))?/i);
    const withMatch = receivedStr.match(/with\s+(\S+)/i);
    // Try multiple date patterns
    const dateMatch = receivedStr.match(/;\s*([^;]+)$/) || receivedStr.match(/\s+([A-Z][a-z]{2},\s+\d{1,2}\s+[A-Z][a-z]{2}\s+\d{4}\s+\d{2}:\d{2}:\d{2}[^;]*)/);

    let from = "unknown";
    if (fromMatch) {
      from = fromMatch[2] ? `${fromMatch[1]} [${fromMatch[2]}]` : fromMatch[1];
    } else if (byMatch && !fromMatch) {
      // For X-Received, "by" might be the source
      from = byMatch[2] ? `${byMatch[1]} [${byMatch[2]}]` : byMatch[1];
    }

    let to = "unknown";
    if (byMatch) {
      to = byMatch[2] ? `${byMatch[1]} [${byMatch[2]}]` : byMatch[1];
    }

    const protocol = withMatch ? withMatch[1].toUpperCase() : "SMTP";
    
    let timeReceived = "";
    let currentTime: Date | null = null;
    if (dateMatch) {
      try {
        const dateStr = dateMatch[1].trim();
        currentTime = new Date(dateStr);
        if (!isNaN(currentTime.getTime())) {
          timeReceived = currentTime.toLocaleString("en-US", {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            timeZoneName: "short",
          });
        }
      } catch (e) {
        // Invalid date
      }
    }

    // Calculate delay (time difference from previous hop)
    let delay = "0 sec";
    if (previousTime && currentTime && !isNaN(currentTime.getTime()) && !isNaN(previousTime.getTime())) {
      // Calculate time difference (previous should be earlier, current later)
      const diffMs = currentTime.getTime() - previousTime.getTime();
      if (diffMs > 0) {
        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const remainingSec = diffSec % 60;
        
        if (diffMin > 0) {
          delay = `${diffMin} min ${remainingSec} sec`;
        } else {
          delay = `${diffSec} sec`;
        }
      }
    }

    chain.push({
      index,
      delay,
      from,
      to,
      protocol,
      timeReceived: timeReceived || "-",
    });

    if (currentTime) {
      previousTime = currentTime;
    }
  });

  return chain;
}

/**
 * Fallback parser for when postal-mime fails
 */
function parseRawEmailFallback(rawMail: string): ParsedEmail {
  // Extract subject - handle multi-line subjects
  let subjectMatch = rawMail.match(/Subject:\s*(.+?)(?:\r?\n(?!\s)|$)/i);
  if (!subjectMatch) {
    // Try without the lookahead
    subjectMatch = rawMail.match(/Subject:\s*(.+?)(?:\r?\n|$)/i);
  }
  // Handle folded headers (subject continues on next line with space/tab)
  let subject = subjectMatch ? subjectMatch[1].trim() : "";
  if (subject) {
    // Remove any continuation lines (lines starting with space/tab after Subject)
    subject = subject.replace(/\r?\n[\s\t]+/g, " ");
  }
  const fromMatch = rawMail.match(/From:\s*(.+?)(?:\r?\n|$)/i);
  const toMatch = rawMail.match(/To:\s*(.+?)(?:\r?\n|$)/i);
  const dateMatch = rawMail.match(/Date:\s*(.+?)(?:\r?\n|$)/i);
  const messageIdMatch = rawMail.match(/Message-ID:\s*(.+?)(?:\r?\n|$)/i);

  // Extract HTML body (content after headers)
  const headerEnd = rawMail.indexOf("\r\n\r\n") !== -1 ? rawMail.indexOf("\r\n\r\n") : rawMail.indexOf("\n\n");
  const body = headerEnd !== -1 ? rawMail.substring(headerEnd + 2) : rawMail;
  const htmlMatch = body.match(/<html[\s\S]*<\/html>/i) || body.match(/<!DOCTYPE[\s\S]*<\/html>/i);
  const html = htmlMatch ? htmlMatch[0] : "";
  const text = html ? html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim() : body.trim();

  // Parse receiving chain from raw mail and extract headers
  const receivedMatches = rawMail.matchAll(/Received:\s*(.+?)(?:\r?\n(?!\s)|$)/gi);
  const receivedHeaders: string[] = [];
  for (const match of receivedMatches) {
    receivedHeaders.push(match[1].trim());
  }
  const headersRecord: Record<string, string | string[]> = {};
  if (receivedHeaders.length > 0) {
    headersRecord["received"] = receivedHeaders;
  }
  
  // Extract sender IP from headers using the same logic
  const senderIP = extractSenderIP(headersRecord, null);
  
  const receivingChain = parseReceivingChain(headersRecord);

  return {
    subject: subject || "",
    html: html || "",
    text: text || "",
    from: fromMatch
      ? {
          address: fromMatch[1].trim(),
        }
      : undefined,
    to: toMatch
      ? {
          address: toMatch[1].trim(),
        }
      : undefined,
    date: dateMatch ? new Date(dateMatch[1].trim()) : undefined,
    messageId: messageIdMatch ? messageIdMatch[1].trim() : "",
    senderIP,
    headers: {},
    receivingChain,
  };
}
